import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { StatusBadge, Detail } from "@/components/common/DashboardBits";
import VisitWorkflow from "@/components/visit/VisitWorkflow";
import VisitSummary from "@/components/visit/VisitSummary";
import LeadDocumentsSection from "@/components/leads/LeadDocumentsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Send, ExternalLink, X } from "lucide-react";
import { safeHttpUrl } from "@/lib/url";

function Section({ title, children }) {
  return (
    <div className="mt-6 border-t border-slate-200 pt-4">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function AssignExecutive({ executives, value, onChange, onAssign, busy, label }) {
  return (
    <div className="flex gap-2 flex-wrap">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[240px]" data-testid="assign-exec-select"><SelectValue placeholder="Select executive" /></SelectTrigger>
        <SelectContent>{executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.working_location}</SelectItem>)}</SelectContent>
      </Select>
      <Button onClick={onAssign} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="assign-exec-button"><UserPlus className="h-4 w-4 mr-1" /> {label}</Button>
    </div>
  );
}

function SubmitToPortal({ bank, link, onLinkChange, onSubmit, busy, label }) {
  const portal = safeHttpUrl(bank?.portal_link);
  return (
    <div className="flex gap-2 flex-wrap items-center">
      <Input placeholder={bank?.portal_link || "https://bank-portal.example.com/submit/..."} value={link} onChange={(e) => onLinkChange(e.target.value)} className="flex-1 min-w-[240px]" data-testid="submit-portal-link" />
      {portal && <a href={portal} target="_blank" rel="noreferrer noopener"><Button variant="outline" type="button"><ExternalLink className="h-4 w-4 mr-1" /> Open Portal</Button></a>}
      <Button onClick={onSubmit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-lead-button"><Send className="h-4 w-4 mr-1" /> {label}</Button>
    </div>
  );
}

/* Lead detail: facts, documents, assignment, inspection workflow / report, submit to bank portal. */
export default function LeadDetailDialog({ lead, onClose, onChanged, executives, banks, role, canAssign, canSubmit, canVisit }) {
  const { t } = useLang();
  const [full, setFull] = useState(lead);
  const [busy, setBusy] = useState(false);
  const [executiveId, setExecutiveId] = useState(lead.assigned_executive_id || "");
  const [submissionLink, setSubmissionLink] = useState(lead.submission_link || "");
  const [editingVisit, setEditingVisit] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/leads/${lead.id}`).then((r) => { if (alive) setFull(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [lead.id]);

  const bank = banks.find((b) => b.id === full.bank_id);
  const showWorkflow = canVisit && (!full.visit || editingVisit);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doAssign = () => (executiveId ? run(() => api.post(`/leads/${lead.id}/assign`, { executive_id: executiveId }), "Assigned") : toast.error("Select an executive"));
  const doSubmit = () => run(() => api.post(`/leads/${lead.id}/status`, { status: "submitted", submission_link: submissionLink || bank?.portal_link || null }), "Submitted to bank portal");

  const facts = [
    [t("property_address"), full.property_address], [t("location"), full.location], [t("branch"), full.branch || "—"],
    [t("valuation_type"), (full.valuation_type || "").toUpperCase()], ["Bank", full.bank_name || "—"], [t("tat"), `${full.tat_hours}h`],
    ["Executive", full.executive_name || "Unassigned"], [t("property_paper"), full.property_paper_ref || "—"],
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">{full.customer_name}<StatusBadge status={full.status} /></DialogTitle>
          <DialogDescription>Lead ID: {full.id}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {facts.map(([label, value]) => <Detail key={label} label={label} value={value} />)}
          {full.notes && <div className="md:col-span-2"><Detail label={t("notes")} value={full.notes} /></div>}
        </div>

        <LeadDocumentsSection lead={full} role={role} onLeadChanged={(patch) => setFull((f) => ({ ...f, ...patch }))} />

        {canAssign && <Section title={t("assign")}><AssignExecutive executives={executives} value={executiveId} onChange={setExecutiveId} onAssign={doAssign} busy={busy} label={t("assign")} /></Section>}

        {showWorkflow && <VisitWorkflow lead={full} onSaved={() => { setEditingVisit(false); onChanged(); }} onCancel={full.visit ? () => setEditingVisit(false) : undefined} />}
        {full.visit && !showWorkflow && <VisitSummary visit={full.visit} role={role} onEdit={canVisit ? () => setEditingVisit(true) : undefined} />}

        {canSubmit && ["visited", "in_progress"].includes(full.status) && (
          <Section title={t("submit_to_portal")}><SubmitToPortal bank={bank} link={submissionLink} onLinkChange={setSubmissionLink} onSubmit={doSubmit} busy={busy} label={t("submit")} /></Section>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="detail-close"><X className="h-4 w-4 mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
